import React, { createContext, useContext, useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';

const FilterContext = createContext(null);

export function useFilters() {
  return useContext(FilterContext);
}

/**
 * Global filter state synced to URL search params.
 * Supports: product, model, subscription, user, status.
 * All filters are optional — empty string means "all".
 */
export function FilterProvider({ children }) {
  const [searchParams, setSearchParams] = useSearchParams();

  const filters = useMemo(() => ({
    product: searchParams.get('product') || '',
    model: searchParams.get('model') || '',
    subscription: searchParams.get('subscription') || '',
    user: searchParams.get('user') || '',
    status: searchParams.get('status') || '',
  }), [searchParams]);

  const setFilter = useCallback((key, value) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      if (value) {
        next.set(key, value);
      } else {
        next.delete(key);
      }
      return next;
    }, { replace: true });
  }, [setSearchParams]);

  const setMultipleFilters = useCallback((updates) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      for (const [key, value] of Object.entries(updates)) {
        if (value) {
          next.set(key, value);
        } else {
          next.delete(key);
        }
      }
      return next;
    }, { replace: true });
  }, [setSearchParams]);

  const clearFilters = useCallback(() => {
    setSearchParams({}, { replace: true });
  }, [setSearchParams]);

  const hasActiveFilters = Object.values(filters).some(Boolean);

  // Build query string for backend API calls
  const toQueryString = useCallback(() => {
    const parts = [];
    if (filters.product) parts.push(`product=${encodeURIComponent(filters.product)}`);
    if (filters.model) parts.push(`model=${encodeURIComponent(filters.model)}`);
    if (filters.subscription) parts.push(`subscription=${encodeURIComponent(filters.subscription)}`);
    if (filters.user) parts.push(`user=${encodeURIComponent(filters.user)}`);
    if (filters.status) parts.push(`status=${encodeURIComponent(filters.status)}`);
    return parts.length > 0 ? '&' + parts.join('&') : '';
  }, [filters]);

  const value = useMemo(() => ({
    filters,
    setFilter,
    setMultipleFilters,
    clearFilters,
    hasActiveFilters,
    toQueryString,
  }), [filters, setFilter, setMultipleFilters, clearFilters, hasActiveFilters, toQueryString]);

  return (
    <FilterContext.Provider value={value}>
      {children}
    </FilterContext.Provider>
  );
}
